import { useCallback } from 'react';
import {
	any,
	compose,
	equals,
	filter,
	flatten,
	forEachObjIndexed,
	has,
	keys,
	mapObjIndexed,
	omit,
	pick,
	pickBy,
	values,
} from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch } from 'react-redux';

import dialog from 'features/common/dialog';
import { setErrors } from 'features/common/submitFormDialog/slice';

// used on SubmitButton onClickError prop
export const useHandleSubmitWrongPolicyNoCDV = ({ continueFn }) =>
	useCallback(
		(formData, errors) => {
			const nonEmptyErrors = filter((error) => !isNilOrEmpty(error), errors);
			const errorKeys = keys(nonEmptyErrors);
			const onlyErrorIsPolicyNumber = errorKeys.length === 1 && errorKeys[0] === 'policyNumber';

			if (onlyErrorIsPolicyNumber) {
				const { policyNumber: psNumberValidations } = pick(['policyNumber'], errors);
				if (psNumberValidations.length === 1 && psNumberValidations[0]?.type === 'WARNING') {
					return continueFn();
				}
			}
		},
		[continueFn],
	);

// used with forms which have warning dialogs on submit check
export const useHandleShowDialogs = ({ page, mapper, continueFn }) => {
	const dispatch = useDispatch();
	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn });

	return useCallback(
		(formData, errors) => {
			const onlyRequiredTypeError = (val) => {
				const hasRequiredError = val.filter((error) => error.type === 'REQUIRED');
				return !isNilOrEmpty(hasRequiredError);
			};

			// getting only errors of type "REQUIRED"
			const requiredTypeErrors = pickBy(onlyRequiredTypeError, errors);

			let finalErrorFields;

			// mapper - when multiple fields are covered by one "substitute" dialog
			if (mapper) {
				const transformToArrayOfFieldUsage = (arrayWithBadFields) => {
					const badFieldsCheckArray = arrayWithBadFields.map((field) => has(field, requiredTypeErrors));
					return badFieldsCheckArray;
				};

				// returns object with substituteKey + array pair (if the "bad fields" are present in requiredTypeErrors)
				const mapperWithCheckedBadFields = mapObjIndexed(transformToArrayOfFieldUsage, mapper);

				const addSubstituteKeyIfOneBadFieldIsPresent = (boolArray, substituteKey) => {
					if (any(equals(true))(boolArray)) {
						requiredTypeErrors[substituteKey] = true;
					}
				};
				// adding substituteKeys to requiredTypeErrors
				forEachObjIndexed(addSubstituteKeyIfOneBadFieldIsPresent, mapperWithCheckedBadFields);

				// getting rid of bad fields and using keys
				const fieldsToDelete = compose(flatten, values)(mapper);
				const cleansedRequiredTypeErrors = omit(fieldsToDelete, requiredTypeErrors);
				const cleansedRequiredTypeErrorsKeys = keys(cleansedRequiredTypeErrors);

				finalErrorFields = cleansedRequiredTypeErrorsKeys;
			} else {
				finalErrorFields = keys(requiredTypeErrors);
			}

			dispatch(setErrors({ page, errors: finalErrorFields }));
			if (!isNilOrEmpty(finalErrorFields)) {
				dispatch(dialog.actions.open(page));
			} else {
				dispatch(dialog.actions.close(page));
				// finally checking the "soft" policyNo warning error
				handleSubmitWrongPolicyNoCDV(formData, errors);
			}
		},
		[dispatch, page, mapper, handleSubmitWrongPolicyNoCDV],
	);
};
